/* eslint-disable camelcase */
import {
  TextField, FormControl, FormHelperText,
} from '@mui/material';
import styled from '@emotion/styled';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import { useAircraftMake } from 'aircraft/hooks/useListAircraftMakes';
import { useAircraftExistence } from 'aircraft/hooks/useCheckAircraftExists';
import { useAircraftMakeModel } from 'aircraft/hooks/useListAircraftMakeModel';
import { useState, useEffect } from 'react';
import { useDebounce } from 'common/hooks/useDebounce';
import { useCanAddToAccount } from 'aircraft/providers/CanAddToAccount';
import ExistingAircraftNotice from 'aircraft/components/AddAircraftModal/ExistingAircraftNotice';

const FoundAccountsHeader = styled.div`
    font-size: 18px;
    margin-bottom: 10xp;
    padding-bottom: 12px;
  `;

const AccountTypeHeader = styled.div`
    font-size: 14px;
    padding-bottom: 24px;
    padding-top: 24px;
  `;

const StyledFormControl = styled(FormControl)`
  margin-top: 10px;
`;

function View({
  accountName,
  aircraftMakes,
  tail,
  handleTailNumber: handleRegNNumber,
  handleChangeMake,
  handleChangeMakeInput,
  handleChangeModel,
  handleChangeModelInput,
  handleChangeSerialNumber,
  aircraftModels,
  serial,
  selectedModel,
  selectedMake,
  aircraftOrAssets,
  showExistingAircraftNotice,
  userOptInAddExisting,
  onUserOptInAddExisting,
  error,
}) {
  return (
    <>
      <FoundAccountsHeader>
        {`Create an aircraft for the ${accountName} account`}
      </FoundAccountsHeader>
      <StyledFormControl fullWidth>
        <Grid container rowSpacing={3} columnSpacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Tail Number"
              name="reg_n_number"
              fullWidth
              size="small"
              type="text"
              value={tail}
              onChange={handleRegNNumber}
              error={!!error}
              required
            />
            {error && <FormHelperText error>{error}</FormHelperText>}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Serial Number"
              name="serial_number"
              fullWidth
              size="small"
              type="text"
              required
              value={serial}
              onChange={handleChangeSerialNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/*
              BT-10206: value/inputValue are plain strings (the make text), and both
              handlers take what MUI passes them. Reading e.target back off the DOM
              stored the typed fragment on keyboard select, so models never loaded.
            */}
            <Autocomplete
              id="make"
              value={selectedMake}
              inputValue={selectedMake}
              options={aircraftMakes}
              filterOptions={(options, { inputValue }) => {
                // Only search once there are 3 or more characters
                if (inputValue.length < 3) {
                  return [];
                }
                const needle = inputValue.toLowerCase();
                return options.filter((option) => option.make.toLowerCase().includes(needle));
              }}
              noOptionsText={selectedMake.length < 3 ? 'Enter at least 3 characters to search...' : 'No matching makes'}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.make)}
              isOptionEqualToValue={(option, value) => option.make === value}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Make" name="make" size="small" required />
              )}
              onChange={handleChangeMake}
              onInputChange={handleChangeMakeInput}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              id="model"
              value={selectedModel}
              inputValue={selectedModel}
              options={aircraftModels}
              getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.model} (Make Model: ${option.aircraft_make_model})`)}
              isOptionEqualToValue={(option, value) => option.model === value}
              noOptionsText={selectedMake ? 'No matching models' : 'Choose a make first'}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Model" name="model" size="small" required />
              )}
              onChange={handleChangeModel}
              onInputChange={handleChangeModelInput}
            />
          </Grid>
        </Grid>
      </StyledFormControl>

      <AccountTypeHeader>
        Note: This aircraft is only visible to super admins until it has a subscription setup in Recurly
      </AccountTypeHeader>

      {showExistingAircraftNotice && (
        <ExistingAircraftNotice
          aircraftOrAssets={aircraftOrAssets}
          regNNumber={tail}
          userOptInAddExisting={userOptInAddExisting}
          onUserOptInAddExisting={onUserOptInAddExisting}
        />
      )}
    </>
  );
}

function Model({
  onValidationChange, userOptInAddExisting, onUserOptInAddExisting, companyId, accountName,
}) {
  const [tail, setTail] = useState('');
  const [error, setError] = useState('');
  const [aircraftSerial, setAircraftSerial] = useState('');
  const [selectedAircraftMake, setSelectedAircraftMake] = useState('');
  const [selectedAircraftModel, setSelectedAircraftModel] = useState('');
  const { updateCanAddToAccount, showExistingAircraftNotice } = useCanAddToAccount();
  const { aircraftMakes } = useAircraftMake();
  const debouncedTail = useDebounce(tail, 750);
  const debouncedAircraftMake = useDebounce(selectedAircraftMake, 750);

  const { aircraftExistence } = useAircraftExistence(debouncedTail, companyId);
  const aircraftMakeModels = useAircraftMakeModel(debouncedAircraftMake);

  const {
    registered_by_other_company,
    registered_by_this_company,
    known,
    serial_number,
    make,
    model,
    aircraft_or_assets = [],
  } = aircraftExistence;

  const handleTailNumber = (e) => {
    setTail(e.target.value?.toUpperCase());
    // Regular expression to match only alphanumeric characters
    const regex = /^[A-Za-z0-9_-]*$/;

    if (regex.test(e.target.value)) {
      setError('');
      onValidationChange('');
    } else {
      setError('Input must contain only letters and numbers, underscores and hyphens.');
      onValidationChange('error');
    }
  };

  const handleChangeSerialNumber = (e) => {
    setAircraftSerial(e.target.value);
  };

  // Option picked (click or keyboard) or cleared. newValue is the option object.
  const handleChangeModel = (e, newValue) => {
    setSelectedAircraftModel(newValue?.model ?? '');
  };

  // Typing. 'reset' echoes the selected option's label back (for a model that is the
  // "(Make Model: …)" display string), so only user input and clears are taken.
  const handleChangeModelInput = (e, newInputValue, reason) => {
    if (reason !== 'reset') {
      setSelectedAircraftModel(newInputValue);
    }
  };

  const handleChangeMake = (e, newValue) => {
    setSelectedAircraftMake(newValue?.make ?? '');
  };

  const handleChangeMakeInput = (e, newInputValue, reason) => {
    if (reason !== 'reset') {
      setSelectedAircraftMake(newInputValue);
    }
  };

  useEffect(() => {
    // If the tail is known and not registered by any company, set the fields to the known values
    if (known && !registered_by_other_company && !registered_by_this_company) {
      setAircraftSerial(serial_number || '');
      setSelectedAircraftMake(make || '');
      setSelectedAircraftModel(model || '');
    }
  }, [known, registered_by_other_company, registered_by_this_company, serial_number, make, model]);

  useEffect(() => {
    // If the tail changes and the aircraft is not known, clear the fields
    if (!known) {
      setAircraftSerial('');
      setSelectedAircraftMake('');
      setSelectedAircraftModel('');
    }
  }, [tail, known]);

  useEffect(() => {
    updateCanAddToAccount({
      registeredByThisCompany: !!registered_by_this_company,
      registeredByOtherCompany: !!registered_by_other_company,
      formIncomplete: tail === '' || aircraftSerial === '' || selectedAircraftMake === '' || selectedAircraftModel === '',
    });
  }, [
    aircraftSerial,
    registered_by_other_company,
    registered_by_this_company,
    selectedAircraftMake,
    selectedAircraftModel,
    updateCanAddToAccount,
    tail,
  ]);

  const hookProps = {
    accountName,
    aircraftMakes,
    tail,
    handleTailNumber,
    handleChangeSerialNumber,
    handleChangeMake,
    handleChangeMakeInput,
    handleChangeModel,
    handleChangeModelInput,
    aircraftModels: aircraftMakeModels,
    serial: aircraftSerial,
    selectedModel: selectedAircraftModel,
    selectedMake: selectedAircraftMake,
    aircraftOrAssets: aircraft_or_assets,
    showExistingAircraftNotice,
    userOptInAddExisting,
    onUserOptInAddExisting,
    error,
  };
  return (
    <View
      {...hookProps}
    />
  );
}

export default Model;
export { View };
